/**
 * Quick Links Component
 * - Clones under Boost collection description when available
 * - Survives Boost re-renders (keeps source outside the mount)
 * - Flickity carousel + keyboard nav
 */

(function () {
  "use strict";

  const CONFIG = {
    flickityOptions: {
      contain: true,
      pageDots: false,
      prevNextButtons: true,
      groupCells: false,
      cellAlign: "left",
      accessibility: true,
      setGallerySize: true,
      dragThreshold: 10,
      freeScroll: false,
    },
    carouselMediaQuery: "(min-width: 769px)",
    selectors: {
      source: ".quick-links__wrapper[data-quick-links-source]",
      wrapper: ".quick-links__wrapper",
      placed: ".quick-links__wrapper[data-quick-links-placed]",
      list: ".quick-links__list",
      link: ".quick-link__link",
      boostMount: ".boost-sd__filter-product-list",
      descriptionWrapper: ".boost-sd__header-description-wrapper",
      description: ".boost-sd__header-description",
      headerContent: ".boost-sd__header-main-2-content",
      header: ".boost-sd__header-main-2",
    },
    classes: {
      loading: "loading",
      hasCarousel: "has-carousel",
      noCarousel: "no-carousel",
      placed: "is-placed",
      initialized: "is-initialized",
      sourceHidden: "is-source-hidden",
    },
  };

  const state = {
    observer: null,
    relocateFrame: null,
    fallbackTimer: null,
  };

  function debounce(fn, wait) {
    let timeout;
    return function (...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  function markSources() {
    document.querySelectorAll(CONFIG.selectors.wrapper).forEach((wrapper) => {
      if (wrapper.hasAttribute("data-quick-links-placed")) return;
      wrapper.setAttribute("data-quick-links-source", "true");
    });
  }

  function getBoostAnchor() {
    const mount = document.querySelector(CONFIG.selectors.boostMount);
    if (!mount) return null;

    // Prefer the whole header block so we sit under the description but
    // outside Boost's inner content click handlers (they preventDefault on nested spans).
    return (
      mount.querySelector(CONFIG.selectors.header) ||
      mount.querySelector(".boost-sd__collection-header") ||
      mount.querySelector(CONFIG.selectors.descriptionWrapper) ||
      mount.querySelector(CONFIG.selectors.description) ||
      mount.querySelector(CONFIG.selectors.headerContent)
    );
  }

  function destroyFlickity(listEl) {
    if (listEl && listEl.flickity) {
      try {
        listEl.flickity.destroy();
      } catch (err) {
        /* ignore */
      }
      listEl.flickity = null;
    }
  }

  function placeClone(source, anchor) {
    const existing = document.querySelector(CONFIG.selectors.placed);
    if (existing && existing.previousElementSibling === anchor) {
      existing.classList.add(CONFIG.classes.placed);
      source.classList.add(CONFIG.classes.sourceHidden);
      return existing;
    }

    if (existing) {
      destroyFlickity(existing.querySelector(CONFIG.selectors.list));
      existing.remove();
    }

    const clone = source.cloneNode(true);
    clone.removeAttribute("data-quick-links-source");
    clone.setAttribute("data-quick-links-placed", "true");
    clone.classList.remove(CONFIG.classes.sourceHidden, CONFIG.classes.initialized);
    clone.classList.add(CONFIG.classes.placed);

    const cloneList = clone.querySelector(CONFIG.selectors.list);
    if (cloneList) {
      cloneList.classList.remove(
        CONFIG.classes.loading,
        CONFIG.classes.hasCarousel,
        CONFIG.classes.noCarousel
      );
      cloneList.flickity = null;
    }

    anchor.insertAdjacentElement("afterend", clone);
    source.classList.add(CONFIG.classes.sourceHidden);
    return clone;
  }

  function showSourceFallback(source) {
    source.classList.remove(CONFIG.classes.sourceHidden);
    source.classList.add(CONFIG.classes.placed);
    setupWrapper(source);
  }

  function relocateQuickLinks() {
    markSources();
    const sources = document.querySelectorAll(CONFIG.selectors.source);
    if (!sources.length) return false;

    const anchor = getBoostAnchor();
    if (!anchor) return false;

    sources.forEach((source) => {
      const placed = placeClone(source, anchor);
      setupWrapper(placed);
    });

    return true;
  }

  function initializeCarousel(listEl, wrapperEl) {
    if (typeof Flickity === "undefined") {
      finalizeStatic(listEl, wrapperEl);
      return null;
    }
    try {
      destroyFlickity(listEl);
      const flkty = new Flickity(listEl, CONFIG.flickityOptions);
      listEl.classList.remove(CONFIG.classes.loading);
      wrapperEl.classList.add(CONFIG.classes.hasCarousel);
      wrapperEl.classList.remove(CONFIG.classes.noCarousel);
      listEl.flickity = flkty;
      return flkty;
    } catch (err) {
      console.error("Flickity init error:", err);
      finalizeStatic(listEl, wrapperEl);
      return null;
    }
  }

  function finalizeStatic(listEl, wrapperEl) {
    listEl.classList.remove(CONFIG.classes.loading);
    wrapperEl.classList.add(CONFIG.classes.noCarousel);
    wrapperEl.classList.remove(CONFIG.classes.hasCarousel);
  }

  function shouldUseCarousel(listEl) {
    const meetsWidth = window.matchMedia(CONFIG.carouselMediaQuery).matches;
    if (!meetsWidth) {
      return false;
    }
    return listEl.scrollWidth > listEl.clientWidth + 1;
  }

  function setupLinks(listEl) {
    const links = listEl.querySelectorAll(CONFIG.selectors.link);
    links.forEach((link, idx) => {
      if (link.dataset.qlKeyNav === "1") return;
      link.dataset.qlKeyNav = "1";

      // Stop Boost header handlers from preventDefault'ing navigation.
      link.addEventListener(
        "click",
        (e) => {
          e.stopPropagation();
        },
        true
      );

      link.addEventListener("keydown", (e) => {
        let target;
        switch (e.key) {
          case "ArrowRight":
            target = (idx + 1) % links.length;
            links[target].focus();
            e.preventDefault();
            break;
          case "ArrowLeft":
            target = (idx - 1 + links.length) % links.length;
            links[target].focus();
            e.preventDefault();
            break;
          case "Home":
            links[0].focus();
            e.preventDefault();
            break;
          case "End":
            links[links.length - 1].focus();
            e.preventDefault();
            break;
        }
      });
    });
  }

  function setupWrapper(wrapper) {
    const list = wrapper.querySelector(CONFIG.selectors.list);
    if (!list) return;

    if (wrapper.classList.contains(CONFIG.classes.initialized)) {
      if (list.flickity) {
        list.flickity.resize();
      }
      return;
    }

    setupLinks(list);
    list.classList.add(CONFIG.classes.loading);

    let flkty = null;
    if (shouldUseCarousel(list)) {
      flkty = initializeCarousel(list, wrapper);
    } else {
      finalizeStatic(list, wrapper);
    }

    window.addEventListener(
      "resize",
      debounce(() => {
        if (!document.body.contains(wrapper)) return;
        const needsCarousel = shouldUseCarousel(list);
        if (needsCarousel && !list.flickity) {
          flkty = initializeCarousel(list, wrapper);
        } else if (!needsCarousel && list.flickity) {
          destroyFlickity(list);
          finalizeStatic(list, wrapper);
        } else if (list.flickity) {
          list.flickity.resize();
        }
      }, 150)
    );

    wrapper.classList.add(CONFIG.classes.initialized);
  }

  function scheduleRelocate() {
    if (state.relocateFrame !== null) return;

    const run = function () {
      state.relocateFrame = null;
      relocateQuickLinks();
    };

    if (window.requestAnimationFrame) {
      state.relocateFrame = window.requestAnimationFrame(run);
    } else {
      state.relocateFrame = window.setTimeout(run, 50);
    }
  }

  function observeBoostMount() {
    if (!window.MutationObserver) return;
    if (state.observer) return;

    const boostRoot =
      document.querySelector(CONFIG.selectors.boostMount) || document.body;

    state.observer = new MutationObserver(function () {
      // Boost often replaces innerHTML — re-place if our clone vanished.
      const placed = document.querySelector(CONFIG.selectors.placed);
      const anchor = getBoostAnchor();
      if (!placed || (anchor && placed.previousElementSibling !== anchor)) {
        scheduleRelocate();
      }
    });

    state.observer.observe(boostRoot, {
      childList: true,
      subtree: true,
    });
  }

  function init() {
    markSources();

    if (relocateQuickLinks()) {
      observeBoostMount();
      return;
    }

    observeBoostMount();
    scheduleRelocate();
    window.addEventListener("load", scheduleRelocate);

    if (state.fallbackTimer) {
      window.clearTimeout(state.fallbackTimer);
    }

    // Fallback: show source above Boost if header never appears.
    state.fallbackTimer = window.setTimeout(function () {
      if (document.querySelector(CONFIG.selectors.placed)) return;

      document.querySelectorAll(CONFIG.selectors.source).forEach(showSourceFallback);
    }, 4000);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", init);
})();
